/* Product-backed homepage ritual picker. Cart submissions use the existing cart.js form handler. */
(() => {
  if (customElements.get('ritual-builder')) return;

  class RitualBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const source = this.querySelector('[data-ritual-variants]');
      if (!source) return;
      try {
        this.variants = JSON.parse(source.textContent);
      } catch (_) {
        return;
      }
      if (!Array.isArray(this.variants) || !this.variants.length) return;

      this.idInput = this.querySelector('[data-ritual-id]');
      if (!this.idInput) return;
      this.groups = [...this.querySelectorAll('[data-ritual-group]')];
      this.image = this.querySelector('[data-ritual-image]');
      this.links = [...this.querySelectorAll('[data-ritual-link]')];
      this.price = this.querySelector('[data-ritual-price]');
      this.compare = this.querySelector('[data-ritual-compare]');
      this.stock = this.querySelector('[data-ritual-stock]');
      this.variantTitles = [...this.querySelectorAll('[data-ritual-title]')];
      this.variantIndices = [...this.querySelectorAll('[data-ritual-index]')];
      this.submit = this.querySelector('[data-ritual-submit]');
      this.submitLabel = this.querySelector('[data-ritual-submit-label]');
      this.quantity = this.querySelector('[data-ritual-quantity]');
      this.status = this.querySelector('[data-ritual-status]');
      this.unavailable = this.querySelector('[data-ritual-unavailable]');
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.addEventListener('change', (event) => {
        if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-ritual-option]')) return;
        const selected = this.groups.map((group) => group.querySelector('input:checked')?.value);
        const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
        this.groups.forEach((group, index) => {
          const label = group.querySelector('[data-ritual-selected]');
          if (label) label.textContent = selected[index] || '';
        });
        this.renderVariant(variant, true);
      }, { signal });

      // Keep the native variant select as the single id field, so cart.js and non-JS POSTs agree.
      // Radios may live outside the form with a form attribute; both remain inside this component.
      // Enhance only after the JSON and controls are usable; a failed script leaves the native picker.
      const initial = this.variants.find((variant) => String(variant.id) === this.idInput.value);
      this.renderVariant(initial, false);
      const options = this.querySelector('[data-ritual-options]');
      if (options) {
        options.hidden = false;
        this.querySelector('[data-ritual-fallback]').hidden = true;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    renderVariant(variant, announce) {
      const label = !variant ? this.dataset.unavailableLabel : variant.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      this.idInput.value = variant ? String(variant.id) : '';
      // Sold-out options remain inspectable, but cannot be submitted.
      if (this.submit) this.submit.disabled = !variant?.available;
      if (this.submitLabel) this.submitLabel.textContent = label;
      if (this.unavailable) this.unavailable.hidden = Boolean(variant);
      if (this.stock) this.stock.textContent = !variant ? this.dataset.unavailableLabel : variant.available ? 'Stokta' : this.dataset.soldOutLabel;
      if (this.price) this.price.textContent = variant ? variant.price : '—';
      if (this.compare) {
        this.compare.hidden = !variant?.compare;
        this.compare.textContent = variant?.compare || '';
      }
      if (this.groups.length) {
        this.variantTitles.forEach((title) => { title.textContent = variant?.title || ''; });
      }
      const variantIndex = variant ? this.variants.findIndex((item) => String(item.id) === String(variant.id)) : -1;
      const indexLabel = variantIndex < 0 ? '—' : String(variantIndex + 1).padStart(2, '0');
      this.variantIndices.forEach((index) => { index.textContent = indexLabel; });
      if (this.quantity) this.quantity.value = String(variant?.quantity || 1);
      this.links.forEach((link) => {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        if (variant) url.searchParams.set('variant', variant.id);
        link.href = url.pathname + url.search;
      });
      if (this.image && variant?.image && this.image.getAttribute('src') !== variant.image) {
        // A previous variant's srcset would otherwise override the new src on high-density screens.
        this.image.removeAttribute('srcset');
        this.image.src = variant.image;
        this.image.alt = variant.alt;
      }
      const error = this.querySelector('[data-form-error]');
      if (error && announce) {
        error.hidden = true;
        error.textContent = '';
      }
      if (announce && this.status) {
        this.status.textContent = variant
          ? `${variant.title}. ${variant.price}. ${variant.available ? 'Stokta.' : this.dataset.soldOutLabel}`
          : this.unavailable?.textContent || this.dataset.unavailableLabel;
      }
    }
  }

  customElements.define('ritual-builder', RitualBuilder);
})();
